import { defineNullableOptionalString, defineOptionalNullableBoolean, defineOptionalNullableNumber, defineRequiredMaxLength, defineRequiredNumber } from "@lib/definitions";
import dayjs from "dayjs";
import { z } from "zod";


export const ProgramSplitCheckInnerRuleConditionValidationSchema = z.object({
    id: z.number().nullish(),
    fieldId: defineOptionalNullableNumber("account attribute"),
    operator: defineRequiredMaxLength("conditional", 200),
    fieldValue: defineRequiredMaxLength("value", 200),
    fieldValue1: defineNullableOptionalString("to"),
    fieldType: defineNullableOptionalString("field type"),
    depth: defineRequiredNumber("depth"),
    thenProgramCode: defineRequiredMaxLength("program code", 200),
    otherFieldName: defineNullableOptionalString("other field name"),
});

export const ProgramSplitInnerRuleConditionValidationSchema = ProgramSplitCheckInnerRuleConditionValidationSchema
    .refine(data => {
        if ((data.fieldId === null || data.fieldId === undefined) && (data.otherFieldName === null || data.otherFieldName === undefined)) {
            return false;
        }
        return true;
    }, {
        path: ["fieldId"],
        message: "The field Id is required."
    })
    .refine(data => {
        if (data.operator !== "BETWEEN") {
            return true;
        }
        return !!data.fieldValue1;
    }, {
        path: [`fieldValue1`],
        message: "The To is required."
    })
    .refine(data => {
        if (!data.fieldValue || !data.fieldValue1 || data.operator !== "BETWEEN") {
            return true;
        }
        if (data.fieldType === "integer" || data.fieldType === "double" || data.fieldType?.startsWith("decimal")) {
            return parseFloat(data.fieldValue1) > parseFloat(data.fieldValue);
        }
        else if (data.fieldType === 'datetime') {
            return dayjs(data.fieldValue1) > dayjs(data.fieldValue);
        }
        else {
            return true
        }
    }, {
        path: ["fieldValue1"],
        message: "To must be greater than From."
    });

export const ProgramSplitCheckRuleConditionValidationSchema = z.object({
    id: z.number().nullish(),
    fieldId: defineOptionalNullableNumber("account attribute"),
    operator: defineRequiredMaxLength("conditional", 200),
    fieldValue: defineRequiredMaxLength("value", 200),
    fieldValue1: defineNullableOptionalString("to"),
    thenProgramCode: defineRequiredMaxLength("program code", 200),
    elseProgramCode: defineNullableOptionalString("program code"),
    fieldType: defineNullableOptionalString("field type"),
    depth: defineRequiredNumber("depth"),
    otherFieldName: defineNullableOptionalString("other field name"),
    innerConditions: z.array(ProgramSplitInnerRuleConditionValidationSchema).nullish()
});

export const ProgramSplitRuleConditionValidationSchema = ProgramSplitCheckRuleConditionValidationSchema
    .refine(data => {
        if ((data.fieldId === null || data.fieldId === undefined) && (data.otherFieldName === null || data.otherFieldName === undefined)) {
            return false;
        }
        return true;
    }, {
        path: ["fieldId"],
        message: "The field Id is required."
    })
    .refine(data => {
        if (data.operator !== "BETWEEN") {
            return true;
        }
        return !!data.fieldValue1;
    }, {
        path: ["fieldValue1"],
        message: "The To is required."
    })
    .refine(data => {
        if (!data.fieldValue || !data.fieldValue1 || data.operator !== "BETWEEN") {
            return true;
        }
        if (data.fieldType === "integer" || data.fieldType === "double" || data.fieldType?.startsWith("decimal")) {
            return parseFloat(data.fieldValue1) > parseFloat(data.fieldValue);
        }
        else if (data.fieldType === 'datetime') {
            return dayjs(data.fieldValue1) > dayjs(data.fieldValue);
        }
        else {
            return true
        }
    }, {
        path: ["fieldValue1"],
        message: "To must be greater than From."
    });

export const ProgramSplitRuleCheckValidationSchema = z.object({
    id: z.number().nullish(),
    elseProgramCode: defineNullableOptionalString("program code"),
    dataStreamSelects: z.array(z.string(), {
        required_error: "Must choose at least one data stream.",
        invalid_type_error: "Must choose at least one data stream.",
    })
        .min(1, { message: "Must choose at least one data stream." }),
    hasProgram: defineOptionalNullableBoolean("has program"),
    conditions: z.array(ProgramSplitRuleConditionValidationSchema).nullish(),
});

export const ProgramSplitRuleValidationSchema = ProgramSplitRuleCheckValidationSchema.refine(data => {
    if (data.hasProgram) {
        return true;
    }
    return data.conditions !== null && data.conditions !== undefined && data.conditions.length > 0;
}, {
    path: ["dataStreamSelects"],
    message: "Must choose at least a condition."
});

export const DataProcessingValidationSchema = z.object({
    id: z.number(),
    name: defineRequiredMaxLength("name", 200),
    clientCode: defineRequiredMaxLength("client", 200),
    processorCode: defineRequiredMaxLength("processor", 200),
    processingRuleId: defineOptionalNullableNumber("account aggregate type code"),
    programSplitRuleMappings: z.array(ProgramSplitRuleValidationSchema).nullish(),
});
